import { useCallback, useState } from "react";
import { Modal } from "antd";
import {
  CloudSyncOutlined,
  DisconnectOutlined,
  LoadingOutlined,
  SyncOutlined,
  WarningOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useEdgeState } from "../hooks/useEdgeMode";
import { useAppSelector } from "../store/hooks";
import {
  EDGE_MANUAL_OVERRIDE_PERMISSION,
  forceEdgeLocalMode,
  releaseEdgeManualHold,
} from "../lib/edge/edgeMode";
import type { EdgeLinkState } from "../store/edgeSlice";

/**
 * Лентата за връзката с облака над „Маркиране", над мониторите и над
 * „Резервации" (docs/edge.md). Наследник на `EdgeModeBanner`, който показваше САМО
 * локалния режим — тоест в облачен режим операторът не виждаше нищо дори
 * когато нищо не работеше и въртележката се въртеше до таймаута на ОС-а.
 *
 * Шестте състояния идват от `edgeSlice.linkState` (пише ги машината в
 * `lib/edge/`, тук само се четат). „online" и липсващо поле не рисуват нищо:
 * лентата е за когато нещо НЕ е наред.
 *
 *  slow            облакът отговаря бавно — чакай, не натискай втори път
 *  suspect         връзката мълчи, проверява се — не повтаряй последното
 *  device_offline  КАСАТА вижда облака жив, значи проблемът е в ТОВА
 *                  устройство. ЧЕРВЕНО и не се маха само — човекът трябва да
 *                  тръгне към кабела/Wi-Fi, а не да чака.
 *  app_down        нашият деплой — минава само по себе си
 *  local           стълбицата „кой какво може" отдолу — освен когато машината
 *                  е подала СОБСТВЕНА причина (не общото `edge.linkLocal`):
 *                  тогава тя печели и лентата става ЧЕРВЕНА. Това е задънената
 *                  улица „касата се върна, а този терминал е сляп", в която
 *                  всяка заявка ще бъде отказана.
 *
 * Полетата `linkState`/`linkMessage` се четат ЗАЩИТЕНО (`readLinkState`):
 * стар кеш на Redux или по-стар билд на машината не бива да чупи екрана на
 * сервитьора — липсва ли полето, лентата просто мълчи.
 */

/**
 * Същият набор, но проверим по време на изпълнение: полето идва от Redux,
 * който може да е кеширан от по-стар билд. Записан е като обект по типа —
 * добави ли някой шесто състояние в `EdgeLinkState`, тук гърми компилаторът,
 * а не сервитьорът пред празна лента.
 */
const LINK_STATES: Record<EdgeLinkState, true> = {
  online: true,
  slow: true,
  suspect: true,
  device_offline: true,
  app_down: true,
  local: true,
};

type Tone = "warn" | "danger";

/** Четене на `linkState` без да се разчита, че полето изобщо съществува. */
function readLinkState(edge: unknown): EdgeLinkState | null {
  const raw = (edge as Record<string, unknown> | null | undefined)?.linkState;
  return typeof raw === "string" &&
    Object.prototype.hasOwnProperty.call(LINK_STATES, raw)
    ? (raw as EdgeLinkState)
    : null;
}

/** Същото за `linkMessage` — само непразен низ се брои за съобщение. */
function readLinkMessage(edge: unknown): string | null {
  const raw = (edge as Record<string, unknown> | null | undefined)?.linkMessage;
  return typeof raw === "string" && raw.trim() !== "" ? raw : null;
}

/**
 * Съобщението, което машината пише при ВСЯКО влизане в локален режим
 * (`edgeMode.ts` → `LINK_MESSAGES.local`). То е общото „работи се срещу
 * касата", а стълбицата отдолу казва повече от него: кой какво може точно
 * сега. Затова САМО то отстъпва пред стълбицата — всяко друго съобщение в
 * локален режим е конкретна причина и печели.
 */
const LOCAL_DEFAULT_MESSAGE = "edge.linkLocal";

interface CloudLinkBannerProps {
  /** „pos" = pos-* токените на „Маркиране"; „default" = семантичната палитра. */
  variant?: "default" | "pos";
  /**
   * Кратко допълнение след текста — „Резервации" подава „данни към 14:32"
   * (кога касата е видяла облака), за да не заема отделен ред на страницата.
   */
  detail?: string | null;
}

export default function CloudLinkBanner({
  variant = "default",
  detail = null,
}: CloudLinkBannerProps) {
  const { t } = useTranslation();
  const edge = useEdgeState();
  const permissions = useAppSelector((state) => state.auth?.permissions);
  // Кой от двата ръчни бутона чака потвърждение: влизане в локален режим или
  // „Върни към облака" (§19.5). Един прозорец, два текста — те засягат ЦЕЛИЯ
  // обект по един и същи начин и искат едно и също право.
  const [confirmAction, setConfirmAction] = useState<"enter" | "leave" | null>(
    null,
  );
  const [switching, setSwitching] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const { mode, cloudReachable, synced, restricted, unresolved, enabled } =
    edge;
  // Касата е задържана локално по РЪЧНО решение (§19.5). Четено защитено:
  // стар кеш на Redux без полето значи „не".
  const manualHold =
    (edge as unknown as { manualOverride?: unknown }).manualOverride === true;
  // Redux `mode` остава ГОСПОДАРЯТ на локалното състояние: той е този, по
  // който се води целият останал екран (disabled действия, `useBills`).
  // `linkState` само добавя петте облачни състояния отгоре.
  const linkState: EdgeLinkState | null =
    mode === "local" ? "local" : readLinkState(edge);

  // Правото е това на „Конфигурация → Устройства" (`manage_devices`), а не
  // това на касовите операции: замразяването спира ЦЕЛИЯ обект. Четем го от
  // Redux, за да се прерисува бутонът при смяна на човека — машината си има
  // своя проверка на същото право и я прави отново при самото извикване.
  const canOverride =
    Array.isArray(permissions) &&
    permissions.some((p) => p?.name === EDGE_MANUAL_OVERRIDE_PERMISSION);

  const runOverride = useCallback(async () => {
    setSwitching(true);
    setOverrideError(null);
    try {
      const outcome = await forceEdgeLocalMode();
      if (outcome === "entered" || outcome === "alreadyLocal") {
        setConfirmAction(null);
        return;
      }
      // По едно съобщение на изход: всяко казва какво да НАПРАВИ човекът.
      const key =
        outcome === "noPermission"
          ? "edge.forceLocalNoPermission"
          : outcome === "disabled"
            ? "edge.forceLocalDisabled"
            : outcome === "noTill"
              ? "edge.forceLocalNoTill"
              : "edge.forceLocalFailed";
      setOverrideError(
        t(key, {
          defaultValue:
            "Превключването не стана. Опитай пак или превключи от касата.",
        }),
      );
    } catch {
      setOverrideError(
        t("edge.forceLocalFailed", {
          defaultValue:
            "Превключването не стана. Опитай пак или превключи от касата.",
        }),
      );
    } finally {
      setSwitching(false);
    }
  }, [t]);

  const runRelease = useCallback(async () => {
    setSwitching(true);
    setOverrideError(null);
    const failedText = t("edge.releaseLocalFailed", {
      defaultValue: "Връщането не стана. Опитай пак или го направи от касата.",
    });
    try {
      const outcome = await releaseEdgeManualHold();
      if (
        outcome === "released" ||
        outcome === "pending" ||
        outcome === "notLocal"
      ) {
        setConfirmAction(null);
        return;
      }
      setOverrideError(
        outcome === "noPermission"
          ? t("edge.forceLocalNoPermission", { defaultValue: failedText })
          : failedText,
      );
    } catch {
      setOverrideError(failedText);
    } finally {
      setSwitching(false);
    }
  }, [t]);

  if (!linkState || linkState === "online") return null;

  // ── Локалният режим: стълбицата остава точно каквато беше ──────────────
  const hasUnresolved = !restricted && unresolved > 0;
  // Ръчното задържане НЕ е „изчаква се касата да изпрати": дневникът може да е
  // празен, а касата пак да стои — докато човек не каже обратното (§19.5).
  const heldByHand = !restricted && !hasUnresolved && manualHold;
  const waitingForSync =
    !restricted && !hasUnresolved && !heldByHand && cloudReachable && !synced;

  let tone: Tone = "warn";
  let icon = <DisconnectOutlined />;
  let kicker = "";
  let text = "";

  if (linkState === "local") {
    icon = hasUnresolved ? (
      <WarningOutlined />
    ) : waitingForSync ? (
      <SyncOutlined spin />
    ) : (
      <DisconnectOutlined />
    );
    kicker = t("edge.bannerTitle");
    text = restricted
      ? t("edge.bannerRestricted")
      : hasUnresolved
        ? t("edge.bannerUnresolved", { count: unresolved })
        : heldByHand
          ? cloudReachable
            ? t("edge.bannerManualHoldCloudBack", {
                defaultValue:
                  "интернетът се върна, но касата е задържана в локален режим ръчно — върни я към облака",
              })
            : t("edge.bannerManualHold", {
                defaultValue: "касата е в локален режим по ръчно решение",
              })
          : waitingForSync
            ? t("edge.bannerSyncing")
            : t("edge.bannerText");
  } else if (linkState === "slow") {
    icon = <LoadingOutlined spin />;
    kicker = t("edge.linkTitleSlow", { defaultValue: "Бавна връзка" });
    text = t("edge.linkSlow", {
      defaultValue: "Връзката е бавна — изчакай, заявката още върви.",
    });
  } else if (linkState === "suspect") {
    icon = <SyncOutlined spin />;
    kicker = t("edge.linkTitleSuspect", { defaultValue: "Проверявам връзката" });
    text = t("edge.linkSuspect", {
      defaultValue: "Проверява се връзката с облака…",
    });
  } else if (linkState === "device_offline") {
    // ЧЕРВЕНО: това е единственото състояние, което човек трябва да ОТИДЕ и
    // да поправи — касата и другите терминали работят, спрял е този.
    tone = "danger";
    icon = <WifiOutlined />;
    kicker = t("edge.linkTitleDeviceOffline", {
      defaultValue: "Само това устройство",
    });
    text = t("edge.linkDeviceOffline", {
      defaultValue:
        "Само това устройство е без интернет — касата е на линия. Провери Wi-Fi-а му; останалите работят нормално.",
    });
  } else if (linkState === "app_down") {
    icon = <CloudSyncOutlined />;
    kicker = t("edge.linkTitleAppDown", { defaultValue: "Облакът се обновява" });
    text = t("edge.linkAppDown", {
      defaultValue:
        "Облакът не отговаря, но мрежата е наред — най-вероятно се обновява. Изчакай малко.",
    });
  }

  // Готовият текст от машината печели пред нашия по подразбиране. В локален
  // режим отстъпва САМО общото съобщение — стълбицата казва повече от него.
  // Досега тук се изхвърляше ВСЯКО съобщение в локален режим и заедно с него
  // единственото, което машината има да каже за задънената улица: касата се е
  // върнала в облака, а този терминал още е локален и всяка негова заявка ще
  // бъде отказана (`edgeMode.ts` → `edge.tillBackTerminalBlind`). Сервитьорът
  // четеше „работиш срещу касата", натискаше „закрий" и получаваше отказ.
  const rawMessage = readLinkMessage(edge);
  const fromStore =
    linkState === "local" && rawMessage === LOCAL_DEFAULT_MESSAGE
      ? null
      : rawMessage;
  if (fromStore) {
    // Договорът допуска и i18n ключ, и готов текст. `t()` връща САМИЯ ключ,
    // когато превод няма — тогава разликата се познава по интервала: ключът е
    // `edge.linkSlow`, изречението има думи. Без този ред липсващ превод
    // изписваше името на ключа на екрана на сервитьора, тоест нещо ПО-ЛОШО от
    // текста по подразбиране, който вече сме сглобили горе.
    const translated = t(fromStore);
    const shown =
      translated !== fromStore
        ? translated
        : /\s/.test(fromStore)
          ? fromStore
          : null;
    if (shown !== null) {
      text = shown;
      if (linkState === "local") {
        // Локален режим СЪС собствена причина значи, че и локалният път не е
        // наред — човекът трябва да стане и да провери устройството, не да
        // чака. Същият тон като „само това устройство", защото това е същият
        // факт, само видян отвътре в локалния режим.
        tone = "danger";
        icon = <WarningOutlined />;
      }
    }
  }

  const palette =
    tone === "danger"
      ? variant === "pos"
        ? "bg-pos-danger-bg text-pos-danger-ink border-pos-danger-brd"
        : "bg-danger-tint text-danger-ink border-danger-tint2"
      : variant === "pos"
        ? "bg-pos-offline-bg text-pos-offline-ink border-pos-offline-brd"
        : "bg-warn-tint text-warn-ink border-warn-tint2";

  // Бутон, който само отказва, е по-лош от липсващ — затова се показва само
  // когато и трите предварителни условия на машината са налице: правото,
  // включен резерв (иначе няма каса, срещу която да се работи) и още не сме
  // в локален режим. При „slow" не се предлага: облакът ОЩЕ отговаря и
  // замразяване на целия обект би било прибързано.
  //
  // В локален режим бутонът остава скрит НАРОЧНО, включително в задънената
  // улица „касата се върна, терминалът е сляп": `forceEdgeLocalMode()` започва
  // с `if (isEdgeLocalMode()) return "alreadyLocal"` — тоест там той е гарантиран
  // празен ход, който само затваря прозореца и не променя нищо. Изходът на
  // човека в това състояние е самото изречение (то казва какво да провери), а
  // не бутон, който не може да го изведе.
  const showOverride =
    canOverride && enabled && linkState !== "local" && linkState !== "slow";
  // „Върни към облака" — обратното на горния, само докато касата е задържана
  // по ръчно решение (§19.5). Същото право: отмяната засяга ЦЕЛИЯ обект.
  // Устройството, натиснало превключването, се връща и само, щом види облака
  // (`edgeMode.ts`); бутонът е за всички останали и за човек, който не чака.
  const showRelease = canOverride && linkState === "local" && heldByHand;

  return (
    <>
      <div
        role="status"
        data-testid="cloud-link-banner"
        data-link-state={linkState}
        data-tone={tone}
        // „assertive" само за червеното: то не се маха само и екранният
        // четец трябва да го каже веднага, а не при следващата пауза.
        aria-live={tone === "danger" ? "assertive" : "polite"}
        className={`flex shrink-0 items-center gap-2.5 border-b px-4 py-[7px] text-[13px] leading-[1.25] ${palette}`}
      >
        <span className="inline-flex shrink-0 items-center justify-center text-[15px]">
          {icon}
        </span>
        <span
          data-testid="cloud-link-kicker"
          className={`shrink-0 text-[11px] font-bold uppercase tracking-[.1em] ${
            variant === "pos" ? "font-pos-mono" : ""
          }`}
        >
          {kicker}
        </span>
        <span aria-hidden className="h-3.5 w-px shrink-0 bg-current opacity-30" />
        <span data-testid="cloud-link-text" className="min-w-0 font-semibold">
          {text}
        </span>
        {detail && (
          <span
            data-testid="cloud-link-detail"
            className="shrink-0 whitespace-nowrap font-medium tabular-nums opacity-80"
          >
            · {detail}
          </span>
        )}
        {showOverride && (
          <button
            type="button"
            data-testid="cloud-link-override"
            onClick={() => {
              setOverrideError(null);
              setConfirmAction("enter");
            }}
            className="ml-auto shrink-0 cursor-pointer rounded-lg border border-current bg-transparent px-2.5 py-[3px] text-[11.5px] font-bold text-current opacity-80 transition-opacity hover:opacity-100"
          >
            {t("edge.forceLocal", {
              defaultValue: "Включи локален режим",
            })}
          </button>
        )}
        {showRelease && (
          <button
            type="button"
            data-testid="cloud-link-release"
            onClick={() => {
              setOverrideError(null);
              setConfirmAction("leave");
            }}
            className="ml-auto shrink-0 cursor-pointer rounded-lg border border-current bg-transparent px-2.5 py-[3px] text-[11.5px] font-bold text-current opacity-80 transition-opacity hover:opacity-100"
          >
            {t("edge.releaseLocal", { defaultValue: "Върни към облака" })}
          </button>
        )}
      </div>

      <Modal
        open={confirmAction !== null}
        onCancel={() => setConfirmAction(null)}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={460}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="flex flex-col gap-3">
          <div className="text-[15px] font-bold text-ink">
            {confirmAction === "leave"
              ? t("edge.releaseLocal", { defaultValue: "Върни към облака" })
              : t("edge.forceLocal", { defaultValue: "Включи локален режим" })}
          </div>
          <p
            data-testid="cloud-link-override-warning"
            className="m-0 text-[13.5px] font-medium leading-[1.55] text-body"
          >
            {confirmAction === "leave"
              ? t("edge.releaseLocalHint", {
                  defaultValue:
                    "Касата изпраща направеното без интернет и целият обект се връща към облака. Направи го само ако интернетът работи на терминалите.",
                })
              : t("edge.forceLocalHint", {
                  defaultValue:
                    "Целият обект минава на работа срещу касата. Направи го само ако си сигурен, че интернетът е спрял.",
                })}
          </p>
          <p className="m-0 text-[13.5px] font-semibold leading-[1.55] text-ink">
            {confirmAction === "leave"
              ? t("edge.releaseLocalQuestion", {
                  defaultValue: "Да върна ли обекта към облака сега?",
                })
              : t("edge.forceLocalQuestion", {
                  defaultValue:
                    "Целият обект минава на работа срещу касата. Да превключа ли сега?",
                })}
          </p>
          {overrideError && (
            <div
              role="alert"
              data-testid="cloud-link-override-error"
              className="rounded-[10px] border border-danger-tint2 bg-danger-tint px-3 py-2.5 text-[12.5px] font-medium leading-[1.4] text-danger-ink"
            >
              {overrideError}
            </div>
          )}
          <div className="mt-1 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              data-testid="cloud-link-override-cancel"
              onClick={() => setConfirmAction(null)}
              className="h-11 cursor-pointer rounded-[10px] border border-shade-2 bg-surface text-[13.5px] font-semibold text-body"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              data-testid="cloud-link-override-confirm"
              disabled={switching}
              onClick={() =>
                void (confirmAction === "leave" ? runRelease() : runOverride())
              }
              className="h-11 cursor-pointer rounded-[10px] border-none bg-brand text-[13.5px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {confirmAction === "leave"
                ? t("edge.releaseLocalConfirm", {
                    defaultValue: "Да, върни към облака",
                  })
                : t("edge.forceLocalConfirm", {
                    defaultValue: "Да, премини на локален режим",
                  })}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
